package top.wcpe.allincore.ui.web.input

internal enum class WebTextInputState {
    IDLE,
    COMPOSING,
    SUPPRESS_POST_COMPOSITION_INPUT,
    SUPPRESS_POST_PASTE_INPUT,
}

internal class WebTextInputStateMachine(private val submitCodePoint: (Int) -> Unit) {
    var state: WebTextInputState = WebTextInputState.IDLE
        private set

    private var pendingComposition: String? = null
    private var suppressedComposition: String? = null

    fun compositionStart(hasTextFocus: Boolean) {
        if (!hasTextFocus) {
            clear()
            return
        }
        pendingComposition = null
        suppressedComposition = null
        state = WebTextInputState.COMPOSING
    }

    fun beforeInput(data: String?, inputType: String, isComposing: Boolean, hasTextFocus: Boolean) {
        if (!hasTextFocus) {
            clear()
            return
        }
        when (state) {
            WebTextInputState.COMPOSING -> updatePendingIfComposition(data, inputType, isComposing)
            WebTextInputState.SUPPRESS_POST_COMPOSITION_INPUT -> handlePostCompositionBeforeInput(data, inputType)
            WebTextInputState.SUPPRESS_POST_PASTE_INPUT -> {
                if (inputType != INSERT_FROM_PASTE) clearSuppression()
            }
            WebTextInputState.IDLE -> Unit
        }
    }

    fun input(data: String?, inputType: String, isComposing: Boolean, hasTextFocus: Boolean) {
        if (!hasTextFocus) {
            clear()
            return
        }
        when (state) {
            WebTextInputState.COMPOSING -> updatePendingIfComposition(data, inputType, isComposing)
            WebTextInputState.SUPPRESS_POST_COMPOSITION_INPUT -> handlePostCompositionInput(data, inputType)
            WebTextInputState.SUPPRESS_POST_PASTE_INPUT -> handlePostPasteInput(data, inputType, isComposing)
            WebTextInputState.IDLE -> submitOrdinaryInput(data, inputType, isComposing)
        }
    }

    fun compositionEnd(data: String?, hasTextFocus: Boolean) {
        if (!hasTextFocus) {
            clear()
            return
        }
        if (state != WebTextInputState.COMPOSING) return
        data?.takeIf { it.isNotEmpty() }?.let(::submit)
        pendingComposition = null
        suppressedComposition = data.orEmpty()
        state = WebTextInputState.SUPPRESS_POST_COMPOSITION_INPUT
    }

    fun paste(text: String?, hasTextFocus: Boolean) {
        if (!hasTextFocus) {
            clear()
            return
        }
        clearSuppression()
        text?.takeIf { it.isNotEmpty() }?.let(::submit)
        state = WebTextInputState.SUPPRESS_POST_PASTE_INPUT
    }

    fun clear() {
        pendingComposition = null
        suppressedComposition = null
        state = WebTextInputState.IDLE
    }

    private fun updatePendingIfComposition(data: String?, inputType: String, isComposing: Boolean) {
        if (isComposing || isCompositionType(inputType)) pendingComposition = data
    }

    private fun handlePostCompositionBeforeInput(data: String?, inputType: String) {
        if (!matchesSuppressedComposition(data, inputType)) clearSuppression()
    }

    private fun handlePostCompositionInput(data: String?, inputType: String) {
        if (matchesSuppressedComposition(data, inputType)) {
            clearSuppression()
            return
        }
        clearSuppression()
        submitOrdinaryInput(data, inputType, isComposing = false)
    }

    private fun handlePostPasteInput(data: String?, inputType: String, isComposing: Boolean) {
        if (inputType == INSERT_FROM_PASTE) {
            clearSuppression()
            return
        }
        clearSuppression()
        submitOrdinaryInput(data, inputType, isComposing)
    }

    private fun matchesSuppressedComposition(data: String?, inputType: String): Boolean {
        val eligibleType = inputType == INSERT_TEXT || isCompositionType(inputType)
        return eligibleType && (data.isNullOrEmpty() || data == suppressedComposition)
    }

    private fun submitOrdinaryInput(data: String?, inputType: String, isComposing: Boolean) {
        if (isComposing || isCompositionType(inputType) || inputType == INSERT_FROM_PASTE) return
        data?.takeIf { it.isNotEmpty() }?.let(::submit)
    }

    private fun submit(text: String) {
        var index = 0
        while (index < text.length) {
            val first = text[index].code
            if (first in HIGH_SURROGATE_RANGE && index + 1 < text.length) {
                val second = text[index + 1].code
                if (second in LOW_SURROGATE_RANGE) {
                    submitCodePoint(toCodePoint(first, second))
                    index += 2
                    continue
                }
            }
            submitCodePoint(first)
            index++
        }
    }

    private fun clearSuppression() {
        suppressedComposition = null
        state = WebTextInputState.IDLE
    }

    private fun isCompositionType(inputType: String): Boolean = inputType.contains("Composition")

    private fun toCodePoint(high: Int, low: Int): Int =
        ((high - HIGH_SURROGATE_START) shl SURROGATE_SHIFT) + (low - LOW_SURROGATE_START) + SUPPLEMENTARY_START

    private companion object {
        const val INSERT_TEXT = "insertText"
        const val INSERT_FROM_PASTE = "insertFromPaste"
        const val HIGH_SURROGATE_START = 0xD800
        const val LOW_SURROGATE_START = 0xDC00
        const val SURROGATE_SHIFT = 10
        const val SUPPLEMENTARY_START = 0x10000
        val HIGH_SURROGATE_RANGE = 0xD800..0xDBFF
        val LOW_SURROGATE_RANGE = 0xDC00..0xDFFF
    }
}
